import { useState, useEffect } from "react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "components/ui/dialog";
import {
  AssetMaintenanceSchedule,
  MaintenanceFrequency,
  MaintenanceFrequencyLabels,
  MaintenanceType,
  MaintenanceTypeLabels,
  UpdateMaintenanceScheduleDto,
} from "shared/types/asset";
import { useUpdateMaintenanceSchedule } from "hooks/useAssets";
import { toast } from "react-toastify";

type DateMode = "next" | "last";

export interface MaintenanceFormData {
  name: string;
  frequency: MaintenanceFrequency;
  customFrequencyMonths?: number;
  maintenanceType: MaintenanceType;
  dateMode: DateMode;
  nextDueDate: string;
  lastCompletedDate: string;
}

export const EMPTY_MAINTENANCE_FORM: MaintenanceFormData = {
  name: "",
  frequency: MaintenanceFrequency.YEARLY,
  maintenanceType: MaintenanceType.PREVENTIVE,
  dateMode: "next",
  nextDueDate: "",
  lastCompletedDate: "",
};

export function scheduleToFormData(
  schedule: AssetMaintenanceSchedule
): MaintenanceFormData {
  const hasLastCompleted = !!schedule.lastCompletedDate;
  return {
    name: schedule.name,
    frequency: schedule.frequency,
    customFrequencyMonths: schedule.customFrequencyMonths,
    maintenanceType: schedule.maintenanceType || MaintenanceType.PREVENTIVE,
    dateMode: hasLastCompleted ? "last" : "next",
    nextDueDate: schedule.nextDueDate
      ? schedule.nextDueDate.split("T")[0]
      : "",
    lastCompletedDate: schedule.lastCompletedDate
      ? schedule.lastCompletedDate.split("T")[0]
      : "",
  };
}

// ==================== Inline Form (for sidebar) ====================

interface MaintenanceFormProps {
  formData: MaintenanceFormData;
  setFormData: (data: MaintenanceFormData) => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
  isEdit: boolean;
}

export function MaintenanceForm({
  formData,
  setFormData,
  onSave,
  onCancel,
  isSaving,
  isEdit,
}: MaintenanceFormProps) {
  return (
    <div className="space-y-3 rounded-lg border p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">
          {isEdit ? "Schema bewerken" : "Nieuw schema"}
        </span>
        <button
          onClick={onCancel}
          className="text-muted-foreground hover:text-foreground"
        >
          &times;
        </button>
      </div>
      <MaintenanceFormFields
        formData={formData}
        setFormData={setFormData}
      />
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Annuleer
        </Button>
        <Button size="sm" onClick={onSave} disabled={isSaving}>
          {isSaving ? "Opslaan..." : "Opslaan"}
        </Button>
      </div>
    </div>
  );
}

// ==================== Edit Dialog (for calendar popover) ====================

interface MaintenanceEditDialogProps {
  schedule: AssetMaintenanceSchedule | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

export function MaintenanceEditDialog({
  schedule,
  open,
  onOpenChange,
  onSaved,
}: MaintenanceEditDialogProps) {
  const { updateSchedule, isUpdating } = useUpdateMaintenanceSchedule();
  const [formData, setFormData] = useState<MaintenanceFormData>(
    EMPTY_MAINTENANCE_FORM
  );

  useEffect(() => {
    if (schedule && open) {
      setFormData(scheduleToFormData(schedule));
    }
  }, [schedule, open]);

  const handleSave = async () => {
    if (!schedule) return;

    if (!formData.name) {
      toast.error("Naam is verplicht");
      return;
    }

    if (formData.dateMode === "next" && !formData.nextDueDate) {
      toast.error("Volgende onderhoudsdatum is verplicht");
      return;
    }

    if (formData.dateMode === "last" && !formData.lastCompletedDate) {
      toast.error("Laatste onderhoudsdatum is verplicht");
      return;
    }

    try {
      const dto: UpdateMaintenanceScheduleDto = {
        name: formData.name,
        frequency: formData.frequency,
        maintenanceType: formData.maintenanceType,
        customFrequencyMonths: formData.customFrequencyMonths,
      };

      if (formData.dateMode === "last") {
        dto.lastCompletedDate = formData.lastCompletedDate;
      } else {
        dto.firstDueDate = formData.nextDueDate;
      }

      await updateSchedule(schedule.id, dto);
      toast.success("Onderhoudsschema bijgewerkt");
      onOpenChange(false);
      onSaved();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Kon onderhoudsschema niet bijwerken";
      toast.error(message);
      console.error("Error updating maintenance schedule:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Schema bewerken</DialogTitle>
          <DialogDescription>
            {schedule?.name}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <MaintenanceFormFields
            formData={formData}
            setFormData={setFormData}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              Annuleer
            </Button>
            <Button size="sm" onClick={handleSave} disabled={isUpdating}>
              {isUpdating ? "Opslaan..." : "Opslaan"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ==================== Shared Form Fields ====================

interface MaintenanceFormFieldsProps {
  formData: MaintenanceFormData;
  setFormData: (data: MaintenanceFormData) => void;
}

export function MaintenanceFormFields({
  formData,
  setFormData,
}: MaintenanceFormFieldsProps) {
  return (
    <>
      {/* Name */}
      <div className="space-y-1">
        <Label className="text-xs">Naam</Label>
        <Input
          placeholder="bv. Jaarlijkse keuring"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
      </div>

      {/* Frequency + Type */}
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Frequentie</Label>
          <Select
            value={formData.frequency}
            onValueChange={(v) =>
              setFormData({
                ...formData,
                frequency: v as MaintenanceFrequency,
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(MaintenanceFrequencyLabels).map(
                ([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Type onderhoud</Label>
          <Select
            value={formData.maintenanceType}
            onValueChange={(v) =>
              setFormData({
                ...formData,
                maintenanceType: v as MaintenanceType,
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(MaintenanceTypeLabels).map(([key, label]) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Custom frequency months */}
      {formData.frequency === MaintenanceFrequency.CUSTOM && (
        <div className="space-y-1">
          <Label className="text-xs">Aantal maanden</Label>
          <Input
            type="number"
            min={1}
            value={formData.customFrequencyMonths || ""}
            onChange={(e) =>
              setFormData({
                ...formData,
                customFrequencyMonths:
                  parseInt(e.target.value) || undefined,
              })
            }
          />
        </div>
      )}

      {/* Date mode toggle */}
      <div className="space-y-2">
        <Label className="text-xs">Datum invullen via</Label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant={formData.dateMode === "next" ? "default" : "outline"}
            size="sm"
            className="flex-1 text-xs"
            onClick={() => setFormData({ ...formData, dateMode: "next" })}
          >
            Volgende datum
          </Button>
          <Button
            type="button"
            variant={formData.dateMode === "last" ? "default" : "outline"}
            size="sm"
            className="flex-1 text-xs"
            onClick={() => setFormData({ ...formData, dateMode: "last" })}
          >
            Laatste datum
          </Button>
        </div>
      </div>

      {/* Date input */}
      {formData.dateMode === "next" ? (
        <div className="space-y-1">
          <Label className="text-xs">Volgende onderhoudsdatum</Label>
          <Input
            type="date"
            value={formData.nextDueDate}
            onChange={(e) =>
              setFormData({ ...formData, nextDueDate: e.target.value })
            }
          />
          <p className="text-[10px] text-muted-foreground">
            Dit wordt de eerstvolgende vervaldatum.
          </p>
        </div>
      ) : (
        <div className="space-y-1">
          <Label className="text-xs">Laatste onderhoudsdatum</Label>
          <Input
            type="date"
            value={formData.lastCompletedDate}
            onChange={(e) =>
              setFormData({
                ...formData,
                lastCompletedDate: e.target.value,
              })
            }
          />
          <p className="text-[10px] text-muted-foreground">
            De volgende datum wordt automatisch berekend op basis van de
            frequentie.
          </p>
        </div>
      )}
    </>
  );
}
